import React, { useState, useEffect } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Switch,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Badge,
  HStack,
  Text,
  Spinner,
  Center,
  Button,
  Box
} from '@chakra-ui/react';
import {
  FiMoreVertical,
  FiEdit2,
  FiCopy,
  FiTrash2,
  FiPlus,
  FiClock,
  FiTag
} from 'react-icons/fi';

const BundleList = ({ onError }) => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  useEffect(() => {
    fetchBundles();
  }, []);

  const fetchBundles = async () => {
    try {
      const response = await fetch(`${qbndAdmin.root}qbnd/v1/bundles`, {
        headers: {
          'X-WP-Nonce': qbndAdmin.nonce
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bundles');
      }

      const data = await response.json();
      console.log('Fetched bundles:', data); // Debug log
      setBundles(data);
    } catch (error) {
      console.error('Error fetching bundles:', error); // Debug log
      onError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (bundleId, currentStatus) => {
    try {
      setUpdatingStatus(bundleId);

      const newStatus = currentStatus === 'publish' ? 'draft' : 'publish';

      const response = await fetch(`${qbndAdmin.root}qbnd/v1/bundle/${bundleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': qbndAdmin.nonce
        },
        body: JSON.stringify({
          status: newStatus
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update bundle status');
      }

      // Update local state immediately
      setBundles(prevBundles =>
        prevBundles.map(bundle =>
          bundle.id === bundleId
            ? { ...bundle, status: newStatus }
            : bundle
        )
      );

    } catch (error) {
      onError(error.message);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDelete = async (bundleId) => {
    if (!window.confirm('Are you sure you want to delete this bundle?')) {
      return;
    }

    try {
      const response = await fetch(`${qbndAdmin.root}qbnd/v1/bundle/${bundleId}`, {
        method: 'DELETE',
        headers: {
          'X-WP-Nonce': qbndAdmin.nonce
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete bundle');
      }

      fetchBundles();
    } catch (error) {
      onError(error.message);
    }
  };

  if (loading) {
    return (
      <Center p={8}>
        <Spinner />
      </Center>
    );
  }

  return (
    <>
      <HStack p={4} bg="gray.50" borderBottom="1px" borderColor="gray.200">
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={() => window.location.href = `${qbndAdmin.adminUrl}admin.php?page=quick-bundles-new`}
        >
          Add New Bundle
        </Button>
      </HStack>

      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Status</Th>
            <Th>Products</Th>
            <Th>Discount</Th>
            <Th>Timer</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {bundles.map(bundle => (
            <Tr key={bundle.id}>
              <Td>
                <Text fontWeight="medium">{bundle.name}</Text>
                {bundle.description && (
                  <Text fontSize="sm" color="gray.600" noOfLines={1}>
                    {bundle.description}
                  </Text>
                )}
              </Td>
              <Td>
                <HStack spacing={2}>
                  <Switch
                    isChecked={bundle.status === 'publish'}
                    onChange={() => handleStatusToggle(bundle.id, bundle.status)}
                    isDisabled={updatingStatus === bundle.id}
                  />
                  <Badge colorScheme={bundle.status === 'publish' ? 'green' : 'gray'}>
                    {updatingStatus === bundle.id ? 'Updating...' : bundle.status === 'publish' ? 'Published' : 'Draft'}
                  </Badge>
                </HStack>
              </Td>
              <Td>
                <Badge colorScheme="blue">
                  {(bundle.products || []).length} products
                </Badge>
              </Td>
              <Td>
                <HStack>
                  <FiTag />
                  <Text>
                    {bundle.discounts?.type === 'percentage'
                      ? `${bundle.discounts.value}% off`
                      : bundle.discounts?.value
                        ? `$${bundle.discounts.value} off`
                        : 'No discount'}
                  </Text>
                </HStack>
              </Td>
              <Td>
                <HStack>
                  <FiClock />
                  <Badge colorScheme={bundle.timer?.active ? 'green' : 'gray'}>
                    {bundle.timer?.active ? 'Active' : 'Inactive'}
                  </Badge>
                </HStack>
              </Td>
              <Td>
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<FiMoreVertical />}
                    variant="ghost"
                    size="sm"
                  />
                  <MenuList>
                    <MenuItem
                      icon={<FiEdit2 />}
                      onClick={() => window.location.href = `${qbndAdmin.adminUrl}admin.php?page=quick-bundles-new&id=${bundle.id}`}
                    >
                      Edit
                    </MenuItem>
                    <MenuItem
                      icon={<FiCopy />}
                      onClick={() => window.location.href = `${qbndAdmin.adminUrl}admin.php?page=quick-bundles-new&duplicate=${bundle.id}`}
                    >
                      Duplicate
                    </MenuItem>
                    <MenuItem
                      icon={<FiTrash2 />}
                      color="red.500"
                      onClick={() => handleDelete(bundle.id)}
                    >
                      Delete
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </>
  );
};

export default BundleList;